import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import api from '../services/api';
import { formatNumber } from '../utils/formatters';
import PiggyBankWithdrawModal from './PiggyBankWithdrawModal';
import SoundManager from '../services/SoundManager';
import tonmoonLogo from '../assets/tonmoon-logo.png';
import usdtLogo from '../assets/usdt-logo.png';
import './PiggyBank.css';

interface PiggyBankData {
    balance: number;
    balanceUsdt: number;
    lastAccrual: string | null;
    annualRate: number;
    dailyRate: number;
}

// Данные о APR для модалки вывода
interface APRPreviewData {
    hasStaking: boolean;
    currentAPR: number;
    initialAPR: number;
    bonusAPR: number;
    accumulatedAPR: number;
    newAPR: number;
    lossAPR: number;
}

const PiggyBank: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useStore();
    const [piggyBankData, setPiggyBankData] = useState<PiggyBankData>({
        balance: 0,
        balanceUsdt: 0,
        lastAccrual: null,
        annualRate: 5.0,
        dailyRate: 0.0137
    });
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState<any[]>([]);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [aprPreview, setAprPreview] = useState<APRPreviewData | null>(null);
    const [showInfoModal, setShowInfoModal] = useState(false); // Модалка с особенностями

    useEffect(() => {
        fetchPiggyBankBalance();
        fetchUserAPR();
    }, []);

    const fetchPiggyBankBalance = async () => {
        try {
            const response = await api.get('/piggybank/balance');
            if (response.data.success) {
                setPiggyBankData({
                    balance: parseFloat(response.data.data.balance),
                    balanceUsdt: parseFloat(response.data.data.balanceUsdt || 0),
                    lastAccrual: response.data.data.lastAccrual,
                    annualRate: response.data.data.annualRate,
                    dailyRate: response.data.data.dailyRate
                });
            }
        } catch (error) {
            console.error('Ошибка загрузки баланса копилки:', error);
        }
    };

    const fetchUserAPR = async () => {
        try {
            // Получаем preview APR для модалки вывода
            const response = await api.get('/piggybank/apr-preview');
            if (response.data.success) {
                const data = response.data.data;
                setAprPreview({
                    hasStaking: data.hasStaking,
                    currentAPR: data.currentAPR,
                    initialAPR: data.initialAPR,
                    bonusAPR: data.bonusAPR,
                    accumulatedAPR: data.accumulatedAPR,
                    newAPR: data.newAPR,
                    lossAPR: data.lossAPR
                });
            }
        } catch (error) {
            console.error('Ошибка загрузки APR preview:', error);
            setAprPreview({
                hasStaking: false,
                currentAPR: 1.0,
                initialAPR: 1.0,
                bonusAPR: 0,
                accumulatedAPR: 0,
                newAPR: 1.0,
                lossAPR: 0
            });
        }
    };

    const handleOpenWithdrawModal = () => {
        if (piggyBankData.balance <= 0 && piggyBankData.balanceUsdt <= 0) {
            alert(t('piggyBank.emptyAlert'));
            return;
        }
        // Звук воспроизводится в модалке при открытии (playModalOpen)
        setWithdrawModalOpen(true);
    };

    const handleConfirmWithdraw = async (amount: number | null) => {
        if (isWithdrawing) return;

        setIsWithdrawing(true);
        setWithdrawModalOpen(false);

        try {
            const response = await api.post('/piggybank/withdraw', {
                amount: amount
            });

            if (response.data.success) {
                const data = response.data.data;
                alert(`✅ ${data.message}\n\n${t('piggyBank.newMainBalance')}: ${data.newMainBalance} SPERM`);
                await fetchPiggyBankBalance();
                await fetchUserAPR();
            }
        } catch (error: any) {
            console.error('Ошибка вывода из копилки:', error);
            alert(error.response?.data?.message || t('piggyBank.withdrawError'));
        } finally {
            setIsWithdrawing(false);
        }
    };

    const fetchHistory = async () => {
        if (showHistory) {
            setShowHistory(false);
            return;
        }

        try {
            const response = await api.get('/piggybank/history?limit=20');
            if (response.data.success) {
                setHistory(response.data.data.history);
                setShowHistory(true);
            }
        } catch (error) {
            console.error('Ошибка загрузки истории:', error);
        }
    };

    return (
        <div className="piggybank-container">
            <div className="piggybank-header">
                <div className="piggybank-title-row">
                    <h2><span className="piggybank-pig-icon">🐷</span> {t('piggyBank.title')}</h2>
                    <button
                        className="piggybank-info-btn"
                        onClick={() => {
                            SoundManager.playButtonClick();
                            setShowInfoModal(true);
                        }}
                        title={t('piggyBank.infoTitle')}
                    >
                        ?
                    </button>
                </div>
                <p className="piggybank-description">
                    {t('piggyBank.description')}
                </p>
            </div>

            <div className="piggybank-balance-card">
                <div className="balance-info">
                    <span className="balance-label">{t('piggyBank.balanceSperm')}:</span>
                    <span className="balance-amount">
                        <img src={tonmoonLogo} alt="SPERM" className="piggy-balance-logo" />
                        {formatNumber(piggyBankData.balance, 2)} SPERM
                    </span>
                </div>
                <div className="balance-info ton-balance">
                    <span className="balance-label">{t('piggyBank.balanceUsdt')}:</span>
                    <span className="balance-amount usdt">
                        <img src={usdtLogo} alt="USDT" className="piggy-balance-logo" />
                        {formatNumber(piggyBankData.balanceUsdt, 2)} USDT
                    </span>
                </div>

                <div className="piggybank-rates">
                    <div className="rate-item">
                        <span className="rate-label">{t('piggyBank.annualRate')}:</span>
                        <span className="rate-value">{piggyBankData.annualRate}%</span>
                    </div>
                    <div className="rate-item">
                        <span className="rate-label">{t('piggyBank.dailyRate')}:</span>
                        <span className="rate-value">{piggyBankData.dailyRate}%</span>
                    </div>
                </div>

                {piggyBankData.lastAccrual && (
                    <div className="last-accrual">
                        {t('piggyBank.lastAccrual')}: {new Date(piggyBankData.lastAccrual).toLocaleDateString()}
                    </div>
                )}
            </div>

            <div className="piggybank-actions">
                <button
                    className="btn-withdraw"
                    onClick={handleOpenWithdrawModal}
                    disabled={isWithdrawing || (piggyBankData.balance <= 0 && piggyBankData.balanceUsdt <= 0)}
                >
                    {isWithdrawing ? t('piggyBank.withdrawing') : t('piggyBank.withdrawButton')}
                </button>

                <button
                    className="btn-history"
                    onClick={fetchHistory}
                >
                    {showHistory ? t('piggyBank.hideHistory') : t('piggyBank.showHistory')}
                </button>
            </div>

            <div className="piggybank-warning">
                ⚠️ {t('piggyBank.warning')}
            </div>

            {showHistory && (
                <div className="piggybank-history">
                    <h3>{t('piggyBank.historyTitle')}</h3>
                    {history.length === 0 ? (
                        <p>{t('piggyBank.historyEmpty')}</p>
                    ) : (
                        <div className="history-list">
                            {history
                                .filter(item => {
                                    // Скрываем записи о процентах меньше 0.01 SPERM (мусорные)
                                    if (item.type === 'piggybank_interest' && parseFloat(item.amount) < 0.01) {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((item, index) => (
                                    <div key={index} className="history-item">
                                        <div className="history-type">
                                            {item.type === 'piggybank_deposit' && `📥 ${t('piggyBank.historyDeposit')}`}
                                            {item.type === 'piggybank_withdrawal' && `📤 ${t('piggyBank.historyWithdrawal')}`}
                                            {item.type === 'piggybank_interest' && `💰 ${t('piggyBank.historyInterest')}`}
                                            {item.type === 'piggybank_transfer_to_slots' && `🎰 ${t('piggyBank.historyTransferToSlots', 'В слоты')}`}
                                        </div>
                                        <div className="history-amount">
                                            {formatNumber(parseFloat(item.amount), 2)} SPERM
                                        </div>
                                        <div className="history-date">
                                            {new Date(item.createdAt).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* Модальное окно вывода */}
            <PiggyBankWithdrawModal
                isOpen={withdrawModalOpen}
                onClose={() => setWithdrawModalOpen(false)}
                balance={piggyBankData.balance}
                balanceUsdt={piggyBankData.balanceUsdt}
                aprPreview={aprPreview}
                onConfirm={handleConfirmWithdraw}
                onSuccess={() => {
                    fetchPiggyBankBalance();
                    fetchUserAPR();
                }}
            />

            {/* Модалка с особенностями копилки */}
            {showInfoModal && (
                <div className="piggybank-info-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="piggybank-info-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="piggybank-info-modal-header">
                            <h3>🐷 {t('piggyBank.featuresTitle')}</h3>
                            <button className="modal-close-btn" onClick={() => {
                                SoundManager.playButtonClick();
                                setShowInfoModal(false);
                            }}>×</button>
                        </div>
                        <div className="piggybank-info-modal-content">
                            <ul>
                                <li>✅ {t('piggyBank.feature1')}</li>
                                <li>✅ {t('piggyBank.feature2')}</li>
                                <li>✅ {t('piggyBank.feature3')}</li>
                                <li>⚠️ {t('piggyBank.feature4')}</li>
                                <li>⚠️ {t('piggyBank.feature5')}</li>
                                <li>💵 {t('piggyBank.feature6')}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PiggyBank;
